import { useSyncExternalStore } from 'react'

export type ThemePreference = 'system' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'
export const THEME_STORAGE_KEY = 'mc.theme'

export function readPreference(): ThemePreference {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY)
    return value === 'light' || value === 'dark' ? value : 'system'
  } catch {
    return 'system'
  }
}

export function writePreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') window.localStorage.removeItem(THEME_STORAGE_KEY)
    else window.localStorage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    // Storage may be disabled; the current page still applies the preference.
  }
}

export function systemPrefersDark(): boolean {
  try {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
  } catch {
    return false
  }
}

export function resolveTheme(preference: ThemePreference, systemDark: boolean): ResolvedTheme {
  if (preference === 'dark') return 'dark'
  if (preference === 'light') return 'light'
  return systemDark ? 'dark' : 'light'
}

export function applyTheme(theme: ResolvedTheme, root: HTMLElement = document.documentElement): void {
  root.classList.toggle('dark', theme === 'dark')
  root.style.colorScheme = theme
}

export type ThemeColorVariable = '--mc-surface' | '--mc-gray-100' | '--mc-gray-200' | '--mc-indigo-500'

export const THEME_COLOR_FALLBACKS: Record<ThemeColorVariable, string> = {
  '--mc-surface': 'rgb(255 255 255)',
  '--mc-gray-100': 'rgb(243 244 246)',
  '--mc-gray-200': 'rgb(229 231 235)',
  '--mc-indigo-500': 'rgb(99 102 241)',
}

export function themeColor(variable: ThemeColorVariable): string {
  try {
    const computed = getComputedStyle(document.documentElement).getPropertyValue(variable).trim()
    if (computed) return `rgb(${computed})`
  } catch {
    // Continue through inline styles to the guaranteed light fallback.
  }
  const inline = document.documentElement.style.getPropertyValue(variable).trim()
  return inline ? `rgb(${inline})` : THEME_COLOR_FALLBACKS[variable]
}

type ThemeSnapshot = Readonly<{ preference: ThemePreference; resolved: ResolvedTheme }>
const subscribers = new Set<() => void>()
const initialPreference = readPreference()
let snapshot: ThemeSnapshot = { preference: initialPreference, resolved: resolveTheme(initialPreference, systemPrefersDark()) }

function publish(preference = readPreference()): ThemeSnapshot {
  snapshot = { preference, resolved: resolveTheme(preference, systemPrefersDark()) }
  applyTheme(snapshot.resolved)
  subscribers.forEach((subscriber) => subscriber())
  return snapshot
}

function subscribe(subscriber: () => void): () => void {
  subscribers.add(subscriber)
  return () => subscribers.delete(subscriber)
}

export function startThemeSync(): () => void {
  publish()
  let media: MediaQueryList | undefined
  try {
    media = window.matchMedia?.('(prefers-color-scheme: dark)')
  } catch {
    media = undefined
  }
  const onMediaChange = () => publish(snapshot.preference)
  const onStorage = (event: StorageEvent) => {
    if (event.key === THEME_STORAGE_KEY) publish()
  }

  media?.addEventListener('change', onMediaChange)
  window.addEventListener('storage', onStorage)

  return () => {
    media?.removeEventListener('change', onMediaChange)
    window.removeEventListener('storage', onStorage)
  }
}

export function useTheme(): {
  preference: ThemePreference
  resolved: ResolvedTheme
  setPreference: (preference: ThemePreference) => void
} {
  const current = useSyncExternalStore(subscribe, () => snapshot, () => snapshot)
  const setPreference = (preference: ThemePreference) => {
    writePreference(preference)
    publish(preference)
  }
  return { ...current, setPreference }
}
